import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '@/lib/config';
import { isNativeAndroid, nativeRequestHeaders, signInWithGoogleNative } from '@/lib/native';
import PrivacyModal from '../components/PrivacyModal';

const formatError = (detail) => {
  if (!detail) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

// Traduz erros técnicos em mensagens úteis sem revelar se um email específico
// existe. Login sempre usa a mesma mensagem para email desconhecido e senha errada.
const friendlyAuthError = (status, detail) => {
  const message = formatError(detail);
  if (status === 401) return 'Email ou senha incorretos.';
  if (status === 429) return 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';
  if (status === 503 && message.toLowerCase().includes('segurança')) return 'A verificação de segurança está temporariamente indisponível. Tente novamente em instantes.';
  if (message.toLowerCase().includes('verificação de segurança necessária')) return 'Conclua a verificação de segurança antes de continuar.';
  if (message.toLowerCase().includes('verificação de segurança inválida')) return 'A verificação de segurança expirou. Faça a verificação novamente.';
  return message;
};

// A API normalmente responde em JSON, mas esta proteção evita mostrar erros
// técnicos como "Unexpected token" caso um proxy ou servidor devolva texto.
const parseApiResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) return response.json();
  const text = await response.text();
  return { detail: response.ok ? text : 'O servidor encontrou um problema. Tente novamente em instantes.' };
};

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // login, register or verify
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileState, setTurnstileState] = useState('loading');
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const turnstileRef = useRef(null);
  const turnstileWidgetRef = useRef(null);
  const googleRef = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const nativeAndroid = isNativeAndroid();
  const desktopFlow = new URLSearchParams(window.location.search).get('desktop_flow');

  const finishGoogleLogin = useCallback(async (credential) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...nativeRequestHeaders() },
      credentials: 'include',
      body: JSON.stringify({
        credential,
        accept_terms: mode === 'register' && acceptedPrivacy,
        turnstile_token: turnstileToken || null,
        birth_date: mode === 'register' ? birthDate : null
      })
    });
    const result = await parseApiResponse(response);
    if (!response.ok) throw new Error(formatError(result.detail));
    await login(result.user, result.access_token);
    if (desktopFlow) {
      const authorize = await fetch(`${BACKEND_URL}/api/auth/desktop/authorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ flow_id: desktopFlow })
      });
      const authorization = await parseApiResponse(authorize);
      if (!authorize.ok) throw new Error(formatError(authorization.detail));
      setNotice('Login concluído! Volte ao aplicativo BlopBoru.');
      return;
    }
    navigate('/', { replace: true });
  }, [acceptedPrivacy, birthDate, desktopFlow, login, mode, navigate, turnstileToken]);

  useEffect(() => {
    if (!turnstileSiteKey) return undefined;
    let cancelled = false;
    let attempts = 0;
    setTurnstileToken('');
    setTurnstileState('loading');

    const renderWidget = () => {
      attempts += 1;
      if (cancelled || turnstileWidgetRef.current !== null) return;
      if (!window.turnstile || !turnstileRef.current) {
        if (attempts >= 40) setTurnstileState('error');
        return;
      }
      try {
        turnstileRef.current.replaceChildren();
        turnstileWidgetRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileSiteKey,
          theme: 'dark',
          appearance: 'always',
          callback: (token) => { setTurnstileToken(token); setTurnstileState('ready'); setError(''); },
          'expired-callback': () => { setTurnstileToken(''); setTurnstileState('expired'); },
          'timeout-callback': () => { setTurnstileToken(''); setTurnstileState('expired'); },
          'error-callback': () => { setTurnstileToken(''); setTurnstileState('error'); }
        });
      } catch (error) {
        console.error('Turnstile render error', error);
        setTurnstileState('error');
      }
    };

    // O script pode já existir e ter terminado de carregar antes deste componente.
    // A repetição curta evita que a caixa de segurança desapareça silenciosamente.
    const retryTimer = window.setInterval(renderWidget, 250);
    const timeoutTimer = window.setTimeout(() => {
      if (!cancelled && !turnstileToken) setTurnstileState(current => current === 'loading' ? 'error' : current);
    }, 15000);
    const existing = document.querySelector('script[data-blop-turnstile]');
    if (existing) {
      renderWidget();
      existing.addEventListener('load', renderWidget);
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.blopTurnstile = 'true';
      script.addEventListener('load', renderWidget);
      document.head.appendChild(script);
    }
    return () => {
      cancelled = true;
      window.clearInterval(retryTimer);
      window.clearTimeout(timeoutTimer);
      const script = document.querySelector('script[data-blop-turnstile]');
      script?.removeEventListener('load', renderWidget);
      if (window.turnstile && turnstileWidgetRef.current !== null) {
        try { window.turnstile.remove(turnstileWidgetRef.current); } catch (_) {}
      }
      turnstileWidgetRef.current = null;
    };
  }, [turnstileAttempt, turnstileSiteKey]);

  useEffect(() => {
    if (!googleClientId || nativeAndroid) return undefined;
    const renderGoogle = () => {
      if (!window.google?.accounts?.id || !googleRef.current) return;
      googleRef.current.replaceChildren();
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async ({ credential }) => {
          setError('');
          setLoading(true);
          try {
            await finishGoogleLogin(credential);
          } catch (err) {
            setError(err.message || 'Não foi possível entrar com Google.');
          } finally {
            setLoading(false);
            if (window.turnstile && turnstileWidgetRef.current !== null) {
              window.turnstile.reset(turnstileWidgetRef.current);
              setTurnstileToken('');
            }
          }
        }
      });
      window.google.accounts.id.renderButton(googleRef.current, {
        theme: 'filled_black',
        size: 'large',
        shape: 'rectangular',
        text: mode === 'login' ? 'signin_with' : 'signup_with',
        width: Math.min(380, googleRef.current.clientWidth || 380),
        locale: 'pt-BR'
      });
    };
    const existing = document.querySelector('script[data-blop-google]');
    if (existing) {
      renderGoogle();
      existing.addEventListener('load', renderGoogle);
      return () => existing.removeEventListener('load', renderGoogle);
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.dataset.blopGoogle = 'true';
    script.addEventListener('load', renderGoogle);
    document.head.appendChild(script);
    return () => script.removeEventListener('load', renderGoogle);
  }, [finishGoogleLogin, googleClientId, mode, nativeAndroid]);

  const handleNativeGoogleLogin = async () => {
    setError('');
    if (mode === 'register' && (!acceptedPrivacy || !birthDate)) {
      setError('Informe sua data de nascimento e aceite os termos antes de criar a conta.');
      return;
    }
    setLoading(true);
    try {
      const result = await signInWithGoogleNative(googleClientId);
      await finishGoogleLogin(result.credential);
    } catch (err) {
      const message = err?.message || 'Não foi possível entrar com Google.';
      setError(message.toLowerCase().includes('cancel') ? 'Login com Google cancelado.' : message);
    } finally {
      setLoading(false);
      if (window.turnstile && turnstileWidgetRef.current !== null) {
        window.turnstile.reset(turnstileWidgetRef.current);
        setTurnstileToken('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    if (mode !== 'verify' && turnstileSiteKey && !turnstileToken) {
      setError(turnstileState === 'error'
        ? 'Não foi possível carregar a verificação de segurança. Atualize a página e tente novamente.'
        : 'Conclua a verificação de segurança antes de continuar.');
      return;
    }
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : mode === 'register' ? '/api/auth/register' : '/api/auth/verify-email';
      const body = mode === 'login'
        ? { email, password, turnstile_token: turnstileToken || null }
        : mode === 'register'
          ? { email, password, name, birth_date: birthDate, accept_terms: acceptedPrivacy, turnstile_token: turnstileToken || null }
          : { email, code: verificationCode };

      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...nativeRequestHeaders() },
        body: JSON.stringify(body),
        credentials: 'include'
      });

      const data = await parseApiResponse(response);

      if (!response.ok) {
        throw new Error(friendlyAuthError(response.status, data.detail));
      }

      if (mode === 'register') {
        setNotice(data.message || 'Enviamos um código para seu e-mail.');
        setMode('verify');
        setPassword('');
      } else if (mode === 'verify') {
        setNotice(data.message || 'E-mail confirmado. Agora você já pode entrar.');
        setMode('login');
        setVerificationCode('');
      } else {
        await login(data.user, data.access_token);
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Erro de conexão. Tente novamente.');
    }
    setLoading(false);
    if (window.turnstile && turnstileWidgetRef.current !== null) {
      window.turnstile.reset(turnstileWidgetRef.current);
      setTurnstileToken('');
    }
  };

  const requestPasswordReset = async () => {
    setError('');
    setNotice('');
    if (!email) {
      setError('Digite seu e-mail primeiro.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, turnstile_token: turnstileToken || null })
      });
      const data = await parseApiResponse(response);
      if (!response.ok) throw new Error(formatError(data.detail));
      setNotice(data.message);
    } catch (err) {
      setError(err.message || 'Não foi possível fazer o pedido agora.');
    } finally {
      setLoading(false);
      if (window.turnstile && turnstileWidgetRef.current !== null) {
        window.turnstile.reset(turnstileWidgetRef.current);
        setTurnstileToken('');
        setTurnstileState('loading');
      }
    }
  };

  const resendVerification = async () => {
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
        method: 'POST', headers: {'Content-Type':'application/json'}, credentials:'include',
        body: JSON.stringify({email, turnstile_token: turnstileToken || null})
      });
      const data = await parseApiResponse(response);
      if (!response.ok) throw new Error(formatError(data.detail));
      setNotice('Se o endereço estiver cadastrado, um novo código será enviado. Aguarde até dois minutos entre pedidos.');
    } catch (err) { setError(err.message || 'Não foi possível reenviar agora.'); }
    finally {
      setLoading(false);
      if (window.turnstile && turnstileWidgetRef.current !== null) {
        window.turnstile.reset(turnstileWidgetRef.current);
        setTurnstileToken('');
      }
    }
  };

  return (
    <div className="blop-login min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="blop-login-grid" aria-hidden="true" />
      <div className="blop-login-glow blop-login-glow-one" aria-hidden="true" />
      <div className="blop-login-glow blop-login-glow-two" aria-hidden="true" />
      <div className="blop-orbit blop-orbit-one" aria-hidden="true" />
      <div className="blop-orbit blop-orbit-two" aria-hidden="true" />

      <div className="blop-sticker blop-sticker-heart" aria-hidden="true">♡</div>
      <div className="blop-sticker blop-sticker-face" aria-hidden="true">•ᴗ•</div>
      <div className="blop-sticker blop-sticker-flower" aria-hidden="true">❀</div>

      <div className="relative w-full max-w-[460px] z-10">
        <div className="text-center mb-7">
          <div className="blop-logo-frame mx-auto mb-4">
            <img src="/blop.png?v=5" alt="Mascote do BlopBoru" className="w-full h-full object-contain" />
          </div>
          <div className="blop-kicker mx-auto mb-3">uma casa para sua arte</div>
          <h1 className="blop-brand text-5xl font-extrabold tracking-tight mb-2">
            Blop<span>Boru</span><b>.</b>
          </h1>
          <p className="text-sm text-white/60">Fanarts, mundos e ideias que merecem ser vistos.</p>
        </div>

        <div className="blop-login-card rounded-[28px] p-6 sm:p-8 animate-scale-in">
          <div className="flex gap-2 mb-6 p-1 rounded-xl bg-white/5">
            <button
              type="button"
              onClick={() => { setMode('login'); setError(''); }}
              data-testid="tab-login"
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                mode === 'login' ? 'blop-tab-active text-white' : 'text-white/40 hover:text-white/70'
              }`}
            >
              Entrar
            </button>
            <button
              type="button"
              onClick={() => { setMode('register'); setError(''); }}
              data-testid="tab-register"
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                mode === 'register' ? 'blop-tab-active text-white' : 'text-white/40 hover:text-white/70'
              }`}
            >
              Cadastrar
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/40 mb-2">
                  Nome
                </label>
                <div className="relative">
                  <i className="ti ti-user absolute left-3 top-1/2 -translate-y-1/2 text-white/40"></i>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome artístico"
                    data-testid="input-name"
                    required={mode === 'register'}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-colors"
                  />
                </div>
              </div>
            )}

            {mode === 'register' && <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/40 mb-2">Data de nascimento</label>
              <div className="relative"><i className="ti ti-calendar-event absolute left-3 top-1/2 -translate-y-1/2 text-white/40"></i><input type="date" value={birthDate} onChange={e=>setBirthDate(e.target.value)} required autoComplete="bday" className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white text-sm focus:outline-none focus:border-white/30"/></div>
              <p className="mt-2 text-[10px] text-white/35">É necessário ter 18 anos. A data completa não será armazenada.</p>
            </div>}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/40 mb-2">
                Email
              </label>
              <div className="relative">
                <i className="ti ti-mail absolute left-3 top-1/2 -translate-y-1/2 text-white/40"></i>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@exemplo.com"
                  data-testid="input-email"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-colors"
                />
              </div>
            </div>

            {mode !== 'verify' && <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/40 mb-2">
                Senha
              </label>
              <div className="relative">
                <i className="ti ti-lock absolute left-3 top-1/2 -translate-y-1/2 text-white/40"></i>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="input-password"
                  required
                  minLength={mode === 'register' ? 10 : 1}
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-colors"
                />
              </div>
            </div>}

            {mode === 'verify' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/40 mb-2">Código de seis números</label>
                <input type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength="6"
                  value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" required
                  className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-center text-2xl tracking-[.4em] text-white placeholder-white/20 focus:outline-none focus:border-white/30" />
                <p className="mt-2 text-center text-xs text-white/40">Confira também a caixa de spam. O código expira em 10 minutos.</p>
              </div>
            )}

            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="error-message">
                <i className="ti ti-alert-circle mr-2"></i>{error}
              </div>
            )}

            {notice && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-200" role="status">
                <i className="ti ti-mail-check mr-2"></i>{notice}
              </div>
            )}

            {turnstileSiteKey && <div className="space-y-2">
              <div ref={turnstileRef} className="flex justify-center" />
              {turnstileState==='loading'&&<p className="text-center text-xs text-white/45"><i className="ti ti-loader-2 mr-1 animate-spin"></i>Carregando proteção de segurança...</p>}
              {turnstileState==='expired'&&<p className="text-center text-xs text-amber-200">A verificação expirou. Marque novamente para continuar.</p>}
              {turnstileState==='error'&&<div className="text-center text-xs text-red-300"><p>A proteção não terminou. Verifique sua conexão.</p><button type="button" className="mt-2 rounded-lg border border-white/15 px-3 py-2 font-semibold text-white/75" onClick={()=>{setError('');setTurnstileToken('');setTurnstileState('loading');setTurnstileAttempt(value=>value+1);}}>Tentar novamente</button></div>}
            </div>}

            {mode === 'register' && (
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-white/[.035] p-3 text-xs leading-5 text-white/55">
                <input
                  type="checkbox"
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  required
                  className="mt-0.5 h-4 w-4 accent-[#9d7be6]"
                />
                <span>
                  Li e concordo com os{' '}
                  <button type="button" onClick={() => setPrivacyOpen(true)} className="font-semibold text-[#b99bf8] underline decoration-[#b99bf8]/40 underline-offset-2 hover:text-white">
                    Termos e a Política de Privacidade
                  </button>.
                </span>
              </label>
            )}

            {googleClientId && mode !== 'verify' && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[.16em] text-white/25">
                  <span className="h-px flex-1 bg-white/10" />
                  ou continue com
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                {nativeAndroid ? (
                  <button
                    type="button"
                    onClick={handleNativeGoogleLogin}
                    disabled={loading}
                    className="flex min-h-[44px] w-full items-center justify-center gap-3 rounded-lg border border-white/15 bg-white px-4 font-semibold text-[#191919] transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5">
                      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.32 2.98-7.41Z" />
                      <path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.36l-3.24-2.54c-.9.6-2.05.96-3.38.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
                      <path fill="#FBBC05" d="M6.39 13.93A6.02 6.02 0 0 1 6.07 12c0-.67.12-1.32.32-1.93V7.45H3.04A10 10 0 0 0 2 12c0 1.61.39 3.14 1.04 4.55l3.35-2.62Z" />
                      <path fill="#EA4335" d="M12 5.94c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.63 9.63 0 0 0 12 2a10 10 0 0 0-8.96 5.45l3.35 2.62C7.18 7.7 9.39 5.94 12 5.94Z" />
                    </svg>
                    {mode === 'login' ? 'Fazer login com o Google' : 'Cadastrar com o Google'}
                  </button>
                ) : (
                  <div ref={googleRef} className="flex min-h-[44px] w-full justify-center overflow-hidden rounded-lg" />
                )}
                <p className="text-center text-[10px] text-white/30">{nativeAndroid?'O login do Google usa a proteção segura do próprio Android.':'Conclua a verificação de segurança acima antes de usar o Google.'}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (mode !== 'verify' && Boolean(turnstileSiteKey) && !turnstileToken)}
              data-testid="submit-button"
              className="blop-primary-button w-full py-3.5 rounded-xl text-sm font-bold transition-all active:scale-[.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin"></div>
                  Carregando...
                </span>
              ) : (
                mode !== 'verify' && turnstileSiteKey && !turnstileToken
                  ? 'Conclua a verificação acima'
                  : mode === 'login' ? 'Entrar no BlopBoru' : mode === 'register' ? 'Criar meu cantinho' : 'Confirmar código'
              )}
            </button>

            {mode === 'login' && (
              <button type="button" onClick={requestPasswordReset} disabled={loading}
                className="w-full text-xs font-semibold text-[#b99bf8] hover:text-white disabled:opacity-50">
                Esqueci minha senha
              </button>
            )}
            {mode === 'verify' && (
              <div className="flex justify-center gap-4 text-xs font-semibold">
                <button type="button" onClick={resendVerification} disabled={loading} className="text-[#b99bf8] hover:text-white disabled:opacity-50">Reenviar código</button>
                <button type="button" onClick={() => setMode('login')} className="text-white/50 hover:text-white">Voltar ao login</button>
              </div>
            )}
          </form>

          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 text-xs text-white/40">
              <i className="ti ti-shield-check text-lg" style={{color:'var(--accent)'}}></i>
              <span>Seus dados são guardados com segurança e informações privadas são criptografadas</span>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-white/30 mt-6">
          Ao continuar, você concorda com nossos Termos de Uso e Política de Privacidade
        </p>
      </div>
      <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}

export default Login;
